import { QueryClientProvider } from "@tanstack/react-query";
import { ReactNode, useEffect, useState } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { AuthProvider } from "./shared/providers/AuthProvider";
import { MobileScreenProvider } from "./shared/providers/MobileScreenProvider";
import ThemeProvider from "./shared/providers/ThemeProvider";
import { AppVersionWatcher } from "./features/app-version/components/AppVersionWatcher";
import { PanelLoadRouteWatcher } from "./features/panel-load-timing/components/PanelLoadRouteWatcher";
import { persistTrackingParams } from "./shared/utils/trackingParams";
import { persistPromoCode, persistSalesEntry } from "./shared/utils/salesEntry";
import { initializeAnalytics } from "./shared/lib/analytics";
import { afterFirstContentfulPaint } from "./shared/utils/afterFirstContentfulPaint";
import { queryClient } from "./core/queryClient";

const fontStylesheetHref =
  "https://fonts.googleapis.com/css2?family=Mulish:ital,wght@0,200..1000;1,200..1000&family=Outfit:wght@100..900&display=swap";

/**
 * Data router com uma rota só (`*`): as `<Routes>` do app continuam
 * descendentes, como no `BrowserRouter` de antes. O data router existe para
 * o `useBlocker` (alterações não salvas), que só ele oferece e que segura
 * também o Voltar/Avançar do navegador.
 */
function createAppRouter(element: ReactNode) {
  return createBrowserRouter([{ path: "*", element }]);
}

const AppProvider = ({ children }: { children?: ReactNode }) => {
  const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
  const shouldEnablePosthog = Boolean(posthogKey) && Boolean(posthogHost);

  useEffect(() => {
    persistTrackingParams();
    // SA-1216: captura o `?vendas=` do link de vendas no boot, antes de qualquer
    // navegação limpar a query string.
    persistSalesEntry();
    // SA-1279: o `?promo=` é independente e combinável — um link pode trazer só
    // o cupom, e a captura tem a mesma janela (o boot).
    persistPromoCode();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const idleWindow = window as Window &
      Partial<{
        requestIdleCallback: (
          callback: IdleRequestCallback,
          options?: IdleRequestOptions
        ) => number;
        cancelIdleCallback: (id: number) => void;
      }>;

    const existingFontStylesheet = document.querySelector(
      `link[data-fonts='superagents'][href='${fontStylesheetHref}']`
    );

    if (existingFontStylesheet) {
      return;
    }

    const loadFonts = () => {
      if (
        document.querySelector(
          `link[data-fonts='superagents'][href='${fontStylesheetHref}']`
        )
      ) {
        return;
      }

      const stylesheet = document.createElement("link");
      stylesheet.rel = "stylesheet";
      stylesheet.href = fontStylesheetHref;
      stylesheet.setAttribute("data-fonts", "superagents");
      document.head.appendChild(stylesheet);
    };

    const scheduleLoad = () => {
      if (idleWindow.requestIdleCallback) {
        const callbackId = idleWindow.requestIdleCallback(loadFonts, {
          timeout: 3500,
        });

        return () => idleWindow.cancelIdleCallback?.(callbackId);
      }

      const timeoutId = window.setTimeout(loadFonts, 2000);

      return () => window.clearTimeout(timeoutId);
    };

    // SA-1474: depois do `load` E do primeiro quadro. No app o `load` vem
    // antes da tela (as rotas são lazy), e as fontes chegavam colado no
    // primeiro quadro: no Lighthouse da calculadora o FCP ia de ~2,5 s para
    // ~4,4 s quando a cadeia até o Google caía antes dele. O texto já aparece
    // na fonte de fallback (`display=swap`); a troca só fica um pouco depois.
    let cancelPaintWait: (() => void) | undefined;
    let cancelIdle: (() => void) | undefined;
    const handleLoad = () => {
      cancelPaintWait = afterFirstContentfulPaint(() => {
        cancelIdle = scheduleLoad();
      });
    };

    if (document.readyState === "complete") {
      handleLoad();
    } else {
      window.addEventListener("load", handleLoad, { once: true });
    }

    return () => {
      window.removeEventListener("load", handleLoad);
      cancelPaintWait?.();
      cancelIdle?.();
    };
  }, []);

  useEffect(() => {
    if (!shouldEnablePosthog || typeof window === "undefined") {
      return;
    }

    const idleWindow = window as Window &
      Partial<{
        requestIdleCallback: (
          callback: IdleRequestCallback,
          options?: IdleRequestOptions
        ) => number;
        cancelIdleCallback: (id: number) => void;
      }>;

    const bootstrapAnalytics = () => {
      initializeAnalytics({
        apiKey: posthogKey,
        apiHost: posthogHost,
      });
    };

    if (idleWindow.requestIdleCallback) {
      const callbackId = idleWindow.requestIdleCallback(bootstrapAnalytics, {
        timeout: 1500,
      });

      return () => idleWindow.cancelIdleCallback?.(callbackId);
    }

    const timeoutId = window.setTimeout(bootstrapAnalytics, 0);

    return () => window.clearTimeout(timeoutId);
  }, [posthogHost, posthogKey, shouldEnablePosthog]);

  // Criado uma vez: o `children` do `main.tsx` é fixo.
  const [router] = useState(() =>
    createAppRouter(
      <>
        <AppVersionWatcher />
        <PanelLoadRouteWatcher />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <MobileScreenProvider>{children}</MobileScreenProvider>
          </AuthProvider>
        </QueryClientProvider>
      </>
    )
  );

  return (
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
};

export default AppProvider;
